import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import deepEqual from 'deep-equal';
import {useNavigationGuard} from './NavigationGuardContext';

const DrawerContext = createContext();

// Drawers that should not trigger the navigation guard when suggestions are active
const UNGUARDED_DRAWERS = ['copilot', 'agent'];

export const DrawerProvider = ({children}) => {
  const [drawerStates, setDrawerStates] = useState({
    comments: false,
    copilot: false,
    history: false,
    agent: false,
    statusHistory: false,
  });
  const [copilotPollingState, setCopilotPollingState] = useState({
    footings: false,
    inconsistencies: false,
  });

  /** Version history blackline UI is in compare mode (two versions + diff). */
  const [isComparingHistory, setIsComparingHistory] = useState(false);

  const {runGuarded} = useNavigationGuard();

  const toggleDrawer = useCallback(
    (drawer) => {
      const action = () => {
        setDrawerStates((prevState) => {
          const isCurrentlyOpen = prevState[drawer];
          const newStates = Object.keys(prevState).reduce((acc, key) => {
            acc[key] = key === drawer ? !isCurrentlyOpen : false;
            return acc;
          }, {});

          return newStates;
        });
      };
      if (UNGUARDED_DRAWERS.includes(drawer)) {
        action();
      } else {
        runGuarded(action);
      }
    },
    [runGuarded]
  );

  const openDrawer = useCallback(
    (drawer) => {
      const action = () => {
        setDrawerStates((prevState) => {
          const newStates = Object.keys(prevState).reduce((acc, key) => {
            acc[key] = key === drawer;
            return acc;
          }, {});

          return newStates;
        });
      };
      if (UNGUARDED_DRAWERS.includes(drawer)) {
        action();
      } else {
        runGuarded(action);
      }
    },
    [runGuarded]
  );

  const closeDrawer = useCallback((drawer) => {
    setDrawerStates((prevState) => ({
      ...prevState,
      [drawer]: false,
    }));
  }, []);

  const isDrawerOpen = useMemo(
    () => Object.values(drawerStates).some((isOpen) => isOpen),
    [drawerStates]
  );

  const getPolling = useCallback(
    (checkType) => copilotPollingState[checkType],
    [copilotPollingState]
  );

  const setPolling = useCallback((checkType, isPolling) => {
    setCopilotPollingState((prevState) => {
      const newState = {
        ...prevState,
        [checkType]: isPolling,
      };
      // Use deep equals to avoid unnecessary state updates
      return deepEqual(prevState, newState) ? prevState : newState;
    });
  }, []);

  const value = useMemo(
    () => ({
      drawerStates,
      toggleDrawer,
      openDrawer,
      closeDrawer,
      isDrawerOpen,
      setPolling,
      getPolling,
      isComparingHistory,
      setIsComparingHistory,
    }),
    [
      drawerStates,
      toggleDrawer,
      openDrawer,
      closeDrawer,
      isDrawerOpen,
      setPolling,
      getPolling,
      isComparingHistory,
      setIsComparingHistory,
    ]
  );

  return (
    <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>
  );
};

DrawerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useDrawer = () => useContext(DrawerContext);
